'use client';

import React, { useState, useEffect } from 'react';
import Image from 'next/image';
import { PortableText } from '@portabletext/react';
import { richTextComponent } from '@/sanity/sanityPortableText';
import { CTAButton } from '@/shadcn/ui/button';
import { GROQImageLoader } from '@/sanity/lib/definitions';
import { cn } from '@/lib/utils';
import { motion, easeOut } from 'framer-motion';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselApi,
} from '@/shadcn/ui/carousel';

import bg from '@/public/global/bg-art.jpg';

import NavBarV2 from '@/components/global/partials/HeaderV2';
import Footer from '@/components/global/partials/Footer';

import { PageAffiliateOverviewSchema } from '@/sanity/pages/PageAffiliateOverviewTemplate';

import bestsBG from '@/public/affiliate-overview/best-card-bg.png';
import calloutBG from '@/public/affiliate-overview/callout_bg.png';

import heroStatue1 from '@/public/affiliate-overview/hero-statue-1.png';
import heroStatue2 from '@/public/affiliate-overview/hero-statue-2.png';
import ethBTC from '@/public/affiliate-overview/eth-btc.svg';
import solana from '@/public/affiliate-overview/solana.svg';
import heroBG from '@/public/affiliate-overview/hero-bg.png';
import lines from '@/public/affiliate-overview/lines.svg';
import lilIcon from '@/public/affiliate-overview/lil-icon.svg';
import iq from '@/public/affiliate-overview/iq-and-you.svg';
import doubleStatues from '@/public/affiliate-overview/double-statues.png';
import lastBG from '@/public/affiliate-overview/last-bg.png';

import GradualBlur from '@/shadcn/GradualBlur';

/* ─── Animation helpers ─────────────────────────────────────────── */

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 20 } as const,
  whileInView: { opacity: 1, y: 0 } as const,
  viewport: { once: true, margin: '-100px' as const },
  transition: { duration: 0.3, delay, ease: [0.25, 0.46, 0.45, 0.94] },
});

const fadeUpBlur = (delay = 0) => ({
  initial: { opacity: 0, y: 30, filter: 'blur(8px)' } as const,
  whileInView: { opacity: 1, y: 0, filter: 'blur(0px)' } as const,
  viewport: { once: true, margin: '-100px' as const },
  transition: { duration: 0.3, delay, ease: [0.25, 0.4, 0.25, 1] },
});

const staggeredFadeUpBlur = (index: number, baseDelay = 0.075) => ({
  initial: { opacity: 0, y: 30, filter: 'blur(8px)' } as const,
  whileInView: { opacity: 1, y: 0, filter: 'blur(0px)' } as const,
  viewport: { once: true, margin: '-100px' as const },
  transition: {
    duration: 0.3,
    delay: baseDelay + index * 0.075,
    ease: [0.25, 0.4, 0.25, 1],
  },
});

const heroSlideUnblur = (
  direction: 'left' | 'right' | 'up',
  delay = 0,
  overrides?: { duration?: number; ease?: number[] | string },
) => ({
  initial: {
    opacity: 0,
    filter: 'blur(12px)',
    x: direction === 'left' ? -80 : direction === 'right' ? 80 : 0,
    y: direction === 'up' ? 60 : 0,
  } as const,
  animate: {
    opacity: 1,
    filter: 'blur(0px)',
    x: 0,
    y: 0,
  } as const,
  transition: {
    duration: overrides?.duration ?? 0.9,
    delay,
    ease: overrides?.ease ?? [0.25, 0.4, 0.25, 1],
  },
});

export default function PageAffiliateOverviewJSX({
  data,
}: {
  data: PageAffiliateOverviewSchema;
}) {
  const [bestsApi, setBestsApi] = useState<CarouselApi>();
  const [bestsCurrent, setBestsCurrent] = useState(0);
  const [bestsCount, setBestsCount] = useState(0);

  const [testimonialsApi, setTestimonialsApi] = useState<CarouselApi>();
  const [testimonialsCurrent, setTestimonialsCurrent] = useState(0);
  const [testimonialsCount, setTestimonialsCount] = useState(0);

  useEffect(() => {
    if (!bestsApi) return;

    const onSelect = () => {
      setBestsCurrent(bestsApi.selectedScrollSnap());
      setBestsCount(bestsApi.scrollSnapList().length);
    };

    onSelect();
    bestsApi.on('select', onSelect);
    bestsApi.on('reInit', onSelect);
    return () => {
      bestsApi.off('select', onSelect);
      bestsApi.off('reInit', onSelect);
    };
  }, [bestsApi]);

  useEffect(() => {
    if (!testimonialsApi) return;

    const onSelect = () => {
      setTestimonialsCurrent(testimonialsApi.selectedScrollSnap());
      setTestimonialsCount(testimonialsApi.scrollSnapList().length);
    };

    onSelect();
    testimonialsApi.on('select', onSelect);
    testimonialsApi.on('reInit', onSelect);
    return () => {
      testimonialsApi.off('select', onSelect);
      testimonialsApi.off('reInit', onSelect);
    };
  }, [testimonialsApi]);

  return (
    <>
      {/* ── Hero ──────────────────────────────────────────── */}
      <section className="relative w-full p-[1rem] lg:p-[4.75rem] !pb-0">
        <div className="relative w-full bg-gradient-to-b lg:px-0 px-[3rem] from-[#F5F8F6] to-[#E6F2EB] rounded-[2rem] overflow-clip h-[99.125rem] lg:h-[70.375rem] lg:rounded-[5.25rem]">
          {data?.settings?.header && (
            <NavBarV2
              headerData={data.settings.header}
              animate={true}
              className="!top-[2rem]"
            />
          )}
          <Image
            src={heroBG}
            alt=""
            className="hidden absolute inset-0 bottom-0 w-full lg:block z-[1]"
          />
          <motion.div
            {...heroSlideUnblur('left', 0.3)}
            className="absolute bottom-0 !translate-y-[20rem] lg:!translate-y-0 right-[-10rem] z-[5] lg:w-[73rem] lg:right-[52.875rem] w-[80rem]"
          >
            <Image src={heroStatue1} alt="" className="w-full" />
          </motion.div>

          <motion.div
            {...heroSlideUnblur('right', 0.4)}
            className="hidden absolute right-0 bottom-0 lg:block w-[73rem] z-[4]"
          >
            <Image src={heroStatue2} alt="" className="w-full" />
          </motion.div>

          <motion.div
            {...heroSlideUnblur('up', 0.1, {
              duration: 0.5,
            })}
            className="hidden absolute lg:bottom-0 lg:block right-[59.375rem] z-[3] w-[7.75rem] hd:bottom-[20%]"
          >
            <Image src={solana} alt="" />
          </motion.div>
          <motion.div
            {...heroSlideUnblur('up', 0.8)}
            className="hidden absolute bottom-0 lg:block right-[31.875rem] z-[6] w-[50.25rem]"
          >
            <Image src={ethBTC} alt="" />
          </motion.div>
          <div className="flex relative flex-col w-full h-full lg:justify-center z-[9] pt-[12rem] lg:px-[21.625rem]">
            {data?.heroText && (
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.3,
                  delay: 0,
                  ease: [0.25, 0.4, 0.25, 1],
                }}
                className="richtext richtext-start max-w-[77.625rem] lg:[&_span]:font-display lg:[&_span]:font-[700] [&_span]:font-instrument-sans [&_span]:font-[600] [&_span]:text-[#103037] lg:[&_span]:text-[#65A38A] lg:[&_br]:block [&_br]:hidden [&_span]:pl-[1rem] lg:[&_span]:pl-0 prose-h1:tracking-[-0.18rem] lg:prose-h1:text-[6rem] prose-h1:text-[6rem] prose-h6:font-instrument prose-h6:font-[400] prose-h6:text-[2.25rem] prose-h6:tracking-[0.09rem] prose-h6:text-black prose-h6:pb-[2rem] prose-p:w-[min(58.125rem,100%)] prose-strong:!font-[600]"
              >
                <PortableText
                  value={data.heroText}
                  components={richTextComponent}
                />
              </motion.div>
            )}

            {data?.heroCta && (
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.3,
                  delay: 0.15,
                  ease: [0.25, 0.4, 0.25, 1],
                }}
                className="mt-[3rem] lg:[&_.button]:!min-h-[5.75rem]"
              >
                <CTAButton data={data.heroCta} />
              </motion.div>
            )}
          </div>
        </div>
      </section>

      {/* ── Deals ─────────────────────────────────────────── */}
      {(data?.dealsTitle || data?.dealsItems) && (
        <section className="relative w-full py-[10rem]">
          <Image
            src={lines}
            alt=""
            className="absolute top-0 left-1/2 -translate-x-1/2 z-[0]"
          />
          <div className="container relative mx-auto px-[3rem] z-[5] lg:px-[4rem]">
            {data?.dealsTitle && (
              <motion.div
                {...fadeUp()}
                className="richtext lg:richtext-center richtext-start mx-auto lg:max-w-[77.625rem] lg:[&_span]:font-display lg:[&_span]:font-[700] [&_span]:font-instrument-sans [&_span]:font-[600] [&_span]:text-[#103037] lg:[&_span]:text-[#65A38A] lg:[&_br]:block [&_br]:hidden [&_span]:pl-[1rem] lg:[&_span]:pl-0 prose-h1:max-w-[60%] lg:prose-h1:max-w-none prose-h1:tracking-[-0.18rem] lg:prose-h1:text-[6rem] prose-h1:text-[4rem] prose-h6:font-instrument prose-h6:font-[400] prose-h6:text-[2.25rem] prose-h6:tracking-[0.09rem] prose-h6:text-black prose-h6:pb-[2rem] prose-p:w-[min(58.125rem,100%)] prose-strong:!font-[600]"
              >
                <PortableText
                  value={data.dealsTitle}
                  components={richTextComponent}
                />
              </motion.div>
            )}
            {data?.dealsItems && data.dealsItems.length > 0 && (
              <React.Fragment>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[2rem] mt-[4rem] w-[min(146.75rem,100%)] mx-auto">
                  {data.dealsItems.slice(0, 3).map((item, idx) => (
                    <motion.div
                      key={idx}
                      {...staggeredFadeUpBlur(idx)}
                      className="flex flex-col text-center bg-white border lg:items-center gap-[1.5rem] rounded-[2rem] p-[3rem]"
                    >
                      {item.icon && (
                        <Image
                          src={item.icon}
                          alt={''}
                          className="object-contain w-[5rem] h-[5rem]"
                        />
                      )}
                      {item.text && (
                        <div className="richtext lg:richtext-center richtext-start prose-h3:text-[3rem] prose-h3:text-[#103037] prose-h3:font-[500] prose-h3:leading-[120%] prose-h3:tracking-[-.72px] prose-h3:capitalize lg:prose-p:text-[2rem] prose-p:text-[2.25rem] lg:prose-p:w-[75%]">
                          <PortableText
                            value={item.text}
                            components={richTextComponent}
                          />
                        </div>
                      )}
                    </motion.div>
                  ))}
                </div>

                <div className="grid grid-cols-1  lg:grid-cols-4 gap-[2rem] mt-[2rem] w-[min(146.75rem,100%)] mx-auto">
                  {data.dealsItems.slice(3, 5).map((item, idx) => (
                    <motion.div
                      key={idx}
                      {...staggeredFadeUpBlur(idx)}
                      className="flex flex-col text-center bg-white border lg:col-span-2 lg:items-center gap-[1.5rem] rounded-[2rem] p-[3rem]"
                    >
                      {item.icon && (
                        <Image
                          src={item.icon}
                          alt={''}
                          className="object-contain w-[5rem] h-[5rem]"
                        />
                      )}
                      {item.text && (
                        <div
                          className={`richtext lg:richtext-center richtext-start prose-h3:text-[3rem] prose-h3:text-[#103037] prose-h3:font-[500] prose-h3:leading-[120%] prose-h3:tracking-[-.72px] prose-h3:capitalize lg:prose-p:text-[2rem] prose-p:text-[2.25rem] ${idx === 0 ? 'lg:prose-p:w-[80%]' : 'lg:prose-p:w-[60%]'}`}
                        >
                          <PortableText
                            value={item.text}
                            components={richTextComponent}
                          />
                        </div>
                      )}
                    </motion.div>
                  ))}
                </div>
              </React.Fragment>
            )}
            {data?.dealsCta && (
              <motion.div
                {...fadeUp(0.15)}
                className="flex justify-center mt-[4rem] lg:[&_.button]:!min-h-[5.75rem]"
              >
                <CTAButton data={data.dealsCta} />
              </motion.div>
            )}
          </div>
        </section>
      )}

      {/* ── Bests ─────────────────────────────────────────── */}
      {(data?.bestsTitle || data?.bestsItems) && (
        <section className="relative w-full lg:p-[4.75rem]">
          <div className="flex relative flex-col bg-[#103037] py-[7rem] gap-[7rem] overflow-clip lg:rounded-[5rem] lg:py-[15rem]">
            <div className="w-[30%] bg-gradient-to-r from-[#103037] to-transparent absolute z-[9] h-full left-0 lg:block hidden pointer-events-none" />
            <div className="w-[30%] bg-gradient-to-l from-[#103037] to-transparent absolute z-[9] h-full right-0 lg:block hidden pointer-events-none" />

            <div className="hidden absolute w-full h-[70%] lg:block pointer-events-none">
              <GradualBlur
                position="right"
                strength={1}
                width="20%"
                divCount={8}
                exponential
                zIndex={99}
                curve="ease-out"
                style={{ overflow: 'hidden' }}
              />
              <GradualBlur
                position="left"
                strength={1}
                width="20%"
                divCount={8}
                exponential
                zIndex={99}
                curve="ease-out"
                style={{ overflow: 'hidden' }}
              />
            </div>
            <div className="lg:px-0 px-[3rem]">
              {data?.bestsTitle && (
                <motion.div
                  {...fadeUp()}
                  className="richtext lg:richtext-center richtext-start mx-auto lg:max-w-[77.625rem] lg:[&_span]:font-display lg:[&_span]:font-[700] [&_span]:font-instrument-sans [&_span]:font-[600] [&_span]:text-white lg:[&_span]:text-[#A3DEB2] lg:[&_br]:block [&_br]:hidden [&_span]:pl-[1rem] lg:[&_span]:pl-0 prose-h1:text-white prose-h1:tracking-[-0.18rem] lg:prose-h1:text-[6rem] prose-h1:text-[4rem] prose-h6:font-instrument prose-h6:font-[400] prose-h6:text-[2.25rem] prose-h6:tracking-[0.09rem] prose-h6:text-white prose-h6:pb-[2rem] prose-p:w-[min(58.125rem,100%)] prose-p:text-white/80 prose-strong:!font-[600]"
                >
                  <PortableText
                    value={data.bestsTitle}
                    components={richTextComponent}
                  />
                </motion.div>
              )}
              {data?.bestsCta && (
                <motion.div
                  {...fadeUp(0.075)}
                  className="flex justify-center mt-[3rem] lg:[&_.button]:!min-h-[5.75rem]"
                >
                  <CTAButton data={data.bestsCta} />
                </motion.div>
              )}
            </div>
            {data?.bestsItems && data.bestsItems.length > 0 && (
              <div className="w-full">
                <Carousel
                  opts={{ loop: true, align: 'center' }}
                  setApi={setBestsApi}
                  className="w-full"
                >
                  <CarouselContent className="-ml-[3rem]">
                    {data.bestsItems.map((item, idx) => (
                      <CarouselItem key={idx} className="pl-[3rem] basis-auto">
                        <div className="flex relative flex-col justify-end text-center lg:items-start gap-[1rem] p-[3rem] bg-[#FFF7E2] rounded-[2rem] h-[33rem] overflow-clip shrink-0 w-[51.875rem]">
                          <Image
                            src={bestsBG}
                            alt=""
                            className="absolute inset-0 z-[0]"
                          />
                          <div className="flex relative flex-col justify-between h-full gap-[2.5rem] z-[1] lg:h-full">
                            {item.invites && (
                              <div className="rounded-full  bg-[#103037]/5 py-[1rem] px-[1.5rem] w-fit">
                                <p className="m-0 font-[500] leading-[150%]">
                                  {item.invites}
                                </p>
                              </div>
                            )}
                            <div className="flex flex-col gap-[2rem] lg:gap-[2.5rem]">
                              <div className="flex items-center gap-[1.25rem]">
                                {item.photo && (
                                  <Image
                                    src={item.photo}
                                    alt={''}
                                    loader={GROQImageLoader}
                                    className="object-cover rounded-full w-[4rem] h-[4rem]"
                                  />
                                )}

                                {item.name && (
                                  <p className="m-0 font-[500] text-[2.25rem] text-[#103037]">
                                    {item.name}
                                  </p>
                                )}
                              </div>
                              <div className="flex items-start flex-col gap-[7.5rem]">
                                {item.value && (
                                  <p className="m-0 font-[600] items-start flex flex-col text-[#103037] lg:text-[4rem] tracking-[-0.12rem] leading-[120%] text-[5rem]">
                                    {item.value}
                                    {item.sufix && (
                                      <span className="capitalize opacity-60 font-[500] text-[2rem] tracking-[-0.06rem]">
                                        {item.sufix}
                                      </span>
                                    )}
                                  </p>
                                )}
                                {/* {item.invites && (
                                  <div className="hidden rounded-full lg:block bg-[#103037]/5 py-[1rem] px-[1.5rem]">
                                    <p className="m-0 font-[500] leading-[150%]">
                                      {item.invites}
                                    </p>
                                  </div>
                                )} */}
                              </div>
                            </div>
                          </div>
                        </div>
                      </CarouselItem>
                    ))}
                  </CarouselContent>
                </Carousel>

                {/* Arrows + Progress Bar */}
                <div className="flex justify-center items-center gap-[2rem] mt-[4rem] px-[2rem]">
                  {/* Previous Button */}
                  <button
                    type="button"
                    onClick={() => bestsApi?.scrollPrev()}
                    className="flex flex-shrink-0 justify-center items-center text-white rounded-full border transition-all w-[6rem] h-[6rem] border-white/20 hover:bg-white/10"
                    aria-label="Previous slide"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      className="flex-shrink-0 h-auto rotate-180 w-[3rem]"
                    >
                      <path
                        d="M13.5 4.5L12.4275 5.54475L18.1125 11.25H3V12.75H18.1125L12.4275 18.4298L13.5 19.5L21 12L13.5 4.5Z"
                        fill="currentColor"
                      />
                    </svg>
                  </button>

                  {/* Progress Bar */}
                  <div className="overflow-hidden w-full rounded-full h-[0.5rem] bg-white/20 max-w-[34rem]">
                    <div
                      className="h-full rounded-full transition-all duration-300 ease-out bg-[#A3DEB2]"
                      style={{
                        width: bestsCount
                          ? `${((bestsCurrent + 1) / bestsCount) * 100}%`
                          : '0%',
                      }}
                    />
                  </div>

                  {/* Next Button */}
                  <button
                    type="button"
                    onClick={() => bestsApi?.scrollNext()}
                    className="flex flex-shrink-0 justify-center items-center text-white rounded-full border transition-all w-[6rem] h-[6rem] border-white/20 hover:bg-white/10"
                    aria-label="Next slide"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      className="flex-shrink-0 h-auto w-[3rem]"
                    >
                      <path
                        d="M13.5 4.5L12.4275 5.54475L18.1125 11.25H3V12.75H18.1125L12.4275 18.4298L13.5 19.5L21 12L13.5 4.5Z"
                        fill="currentColor"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* ── Partnership ───────────────────────────────────── */}
      {(data?.partnershipTitle || data?.partnershipItems) && (
        <section className="relative w-full p-[3rem] lg:p-[4.75rem]">
          <div className="relative w-full overflow-clip pb-[4rem] pt-[4rem] lg:pb-[20rem] lg:pt-[12rem] lg:bg-[#F0F4F2] lg:px-[4rem] lg:rounded-[5rem]">
            <Image
              src={calloutBG}
              alt=""
              className="hidden absolute right-0 bottom-0 left-0 lg:block z-[0]"
            />
            <div className="w-[min(149rem,100%)] mx-auto flex flex-col lg:gap-[6rem] relative z-[1] gap-[4rem]">
              <div>
                {data?.partnershipTitle && (
                  <motion.div
                    {...fadeUp()}
                    className="richtext lg:richtext-center richtext-start mx-auto w-[min(100rem,100%)] lg:[&_span]:font-display lg:[&_span]:font-[700] [&_span]:font-instrument-sans [&_span]:font-[600] [&_span]:text-[#103037] lg:[&_span]:text-[#65A38A] lg:[&_br]:block [&_br]:hidden [&_span]:pl-[1rem] lg:[&_span]:pl-0 prose-h1:tracking-[-0.18rem] lg:prose-h1:text-[6rem] prose-h1:text-[4rem] prose-h6:font-instrument prose-h6:font-[400] prose-h6:text-[2.25rem] prose-h6:tracking-[0.09rem] prose-h6:text-black prose-h6:pb-[2rem] prose-p:w-[min(58.125rem,100%)] prose-strong:!font-[600]"
                  >
                    <PortableText
                      value={data.partnershipTitle}
                      components={richTextComponent}
                    />
                  </motion.div>
                )}
              </div>
              <div>
                {data?.partnershipItems && data.partnershipItems.length > 0 && (
                  <div className="flex flex-col gap-[2rem]">
                    {data.partnershipItems.map((item, idx) => (
                      <motion.div
                        key={idx}
                        {...staggeredFadeUpBlur(idx)}
                        className="flex overflow-hidden relative items-center gap-[2rem] rounded-[2rem] lg:rounded-[4rem] lg:p-[1rem]"
                        style={
                          item.color ? { backgroundColor: item.color } : {}
                        }
                      >
                        {item.bg && (
                          <Image
                            src={item.bg}
                            alt=""
                            loader={GROQImageLoader}
                            className="object-cover !h-full w-[54.125rem] lg:block hidden"
                            quality={100}
                          />
                        )}
                        <div className="relative z-10 w-[min(85rem,100%)] lg:py-[3rem] lg:pl-[2rem] p-[3rem] lg:p-0">
                          {item.text && (
                            <div className="richtext richtext-start prose-h3:text-[3.5rem] prose-h3:font-[500] prose-h6:bg-[#103037] prose-h6:p-[0.75rem] prose-h6:rounded-[0.875rem] prose-h6:text-[2.25rem] prose-h6:font-[500] prose-h6:mb-[2rem] prose-h6:text-white prose-p:text-[2.25rem] prose-p:font-[400] prose-p:leading-[145%] prose-p:m-0 prose-ul:pl-0 prose-ul:my-[1rem] prose-ul:m-0 prose-li:text-[2.25rem] prose-li:text-[#103037]">
                              <PortableText
                                value={item.text}
                                components={richTextComponent}
                              />
                            </div>
                          )}
                          {item.cta && idx !== 1 && (
                            <div className="mt-[2rem] [&_*]:!text-white lg:[&_.button]:!min-h-[5.75rem]">
                              <CTAButton data={item.cta} className="" />
                            </div>
                          )}
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}

                {/* Callout */}
                {data?.partnershipCallout && (
                  <motion.div
                    {...fadeUpBlur(0.15)}
                    className="flex flex-col justify-between items-center lg:flex-row bg-[#103037] mt-[2rem] p-[3rem] gap-[3rem] rounded-[2rem] lg:rounded-[4rem] lg:p-[4rem]"
                  >
                    <div className="flex flex-col gap-[3rem]">
                      {data.partnershipCallout.text && (
                        <motion.div
                          {...fadeUp(0.225)}
                          className="richtext lg:richtext-start richtext-center:[&_span]:text-[#A3DEB2] [&_span]:text-white w-[min(44rem,100%)] lg:[&_span]:font-display lg:[&_span]:font-[700] [&_span]:font-instrument-sans [&_span]:font-[500] prose-h1:!font-geist prose-h3:text-white prose-h3:font-[500]"
                        >
                          <PortableText
                            value={data.partnershipCallout.text}
                            components={richTextComponent}
                          />
                        </motion.div>
                      )}
                      {data.partnershipCallout.cta && (
                        <motion.div
                          {...fadeUp(0.3)}
                          className="lg:[&_.button]:!min-h-[5.75rem] lg:block hidden"
                        >
                          <CTAButton data={data.partnershipCallout.cta} />
                        </motion.div>
                      )}
                    </div>
                    {data.partnershipCallout.steps &&
                      data.partnershipCallout.steps.length > 0 && (
                        <div className="flex relative flex-col justify-center w-[min(62.75rem,100%)] gap-[2rem]">
                          <motion.div
                            initial={{ opacity: 0, filter: 'blur(8px)' }}
                            whileInView={{ opacity: 1, filter: 'blur(0px)' }}
                            viewport={{ once: true, margin: '-100px' }}
                            transition={{
                              duration: 0.3,
                              delay: 0.3 + 1 * 0.075,
                              ease: [0.25, 0.4, 0.25, 1],
                            }}
                            className="absolute top-0 translate-y-[145%] w-[.25rem] h-[5.5rem] bg-[#A3DEB2] lg:left-[7.5%] left-[11%]"
                          />
                          <motion.div
                            initial={{ opacity: 0, filter: 'blur(8px)' }}
                            whileInView={{ opacity: 1, filter: 'blur(0px)' }}
                            viewport={{ once: true, margin: '-100px' }}
                            transition={{
                              duration: 0.3,
                              delay: 0.3 + 2 * 0.075,
                              ease: [0.25, 0.4, 0.25, 1],
                            }}
                            className="absolute top-0 translate-y-[360%] w-[.25rem] h-[5.5rem] bg-[#A3DEB2] lg:left-[7.5%] left-[11%]"
                          />
                          {data.partnershipCallout.steps.map((step, idx) => (
                            <motion.div
                              key={idx}
                              {...staggeredFadeUpBlur(idx, 0.3)}
                              className="flex items-center gap-[2.25rem] bg-white/[8%] border border-white/10 rounded-[1.5rem] p-[1.5rem]"
                            >
                              <div className="rounded-full border-[2px] border-[#A3DEB2] p-[.5rem]">
                                <div className="rounded-full border w-[5rem] h-[5rem] border-white/[15%] flex items-center justify-center">
                                  <p className="m-0 text-[#A3DEB2] text-[2.25rem]">
                                    {idx + 1}
                                  </p>
                                </div>
                              </div>
                              <p className="m-0 text-[2.25rem] text-white/90">
                                {step.text}
                              </p>
                            </motion.div>
                          ))}
                        </div>
                      )}

                    {data.partnershipCallout.cta && (
                      <motion.div
                        {...fadeUp(0.3)}
                        className="lg:[&_.button]:!min-h-[5.75rem] lg:hidden w-full"
                      >
                        <CTAButton data={data.partnershipCallout.cta} />
                      </motion.div>
                    )}
                  </motion.div>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ── Growth ────────────────────────────────────────── */}
      {(data?.growthTitle || data?.growthItems) && (
        <section className="relative w-full py-[2rem] lg:py-[10rem]">
          <div className="container flex flex-col mx-auto lg:px-0 lg:pb-0 px-[3rem] gap-[4rem] pb-[4rem]">
            {data?.growthTitle && (
              <motion.div
                {...fadeUp()}
                className="richtext lg:richtext-center richtext-start lg:mx-auto lg:w-[min(64.25rem,100%)] w-[80%] lg:[&_span]:font-display lg:[&_span]:font-[700] [&_span]:font-instrument-sans [&_span]:font-[600] [&_span]:text-[#103037] lg:[&_span]:text-[#65A38A] lg:[&_br]:block [&_br]:hidden [&_span]:pl-[1rem] lg:[&_span]:pl-0 prose-h1:tracking-[-0.18rem] lg:prose-h1:text-[6rem] prose-h1:text-[4rem] prose-h6:font-instrument prose-h6:font-[400] prose-h6:text-[2.25rem] prose-h6:tracking-[0.09rem] prose-h6:text-black prose-h6:pb-[2rem] prose-p:w-[min(58.125rem,100%)] prose-strong:!font-[600]"
              >
                <PortableText
                  value={data.growthTitle}
                  components={richTextComponent}
                />
              </motion.div>
            )}
            {data?.growthItems && data.growthItems.length > 0 && (
              <div className="grid lg:grid-cols-3 w-[min(180rem,100%)] gap-[2rem] items-end grid-cols-1 mx-auto">
                {data.growthItems.map((item, idx) => (
                  <motion.div
                    key={idx}
                    {...staggeredFadeUpBlur(idx)}
                    className={`overflow-hidden relative ${idx === 0 ? 'lg:h-[73rem] h-[59rem]' : idx === 1 ? 'lg:h-[77.75rem] h-[72rem] lg:mb-[8rem]' : 'lg:h-[85.25rem] h-[82.5rem] lg:mb-[16rem]'} rounded-[3rem] p-[3rem]`}
                    style={{
                      background:
                        'linear-gradient(180deg, rgba(251, 251, 251, 0.50) -79.86%, rgba(177, 194, 173, 0.50) 80.31%)',
                    }}
                  >
                    {item.bg1 && (
                      <Image
                        src={item.bg1}
                        alt=""
                        quality={100}
                        className="hidden absolute right-0 bottom-0 left-0 w-full lg:block mix-blend-luminosity"
                      />
                    )}
                    {item.bg2 && (
                      <Image
                        src={item.bg2}
                        alt=""
                        quality={100}
                        className="absolute right-0 bottom-0 left-1/2 -translate-x-1/2 lg:scale-100 lg:translate-y-0"
                      />
                    )}
                    <div className="flex relative z-10 flex-col gap-[2rem]">
                      <div className="richtext richtext-start [&_span]:text-[#103037] prose-h3:text-[3.5rem] prose-h3:font-[600] prose-p:text-[2.25rem]">
                        <PortableText
                          value={item.title}
                          components={richTextComponent}
                        />
                      </div>

                      <hr className="border-t h-[.125rem] border-black/[15%]" />

                      <div className="flex flex-col gap-[1rem]">
                        {item.checks.map((check, index) => (
                          <div
                            key={index}
                            className="flex items-center gap-[1.5rem]"
                          >
                            <svg
                              width="24"
                              height="24"
                              className="!shrink-0 w-[3rem] h-[3rem]"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <rect
                                width="24"
                                height="24"
                                fill="white"
                                fillOpacity="0.01"
                                className="mix-blend-multiply"
                              />
                              <path
                                d="M9.75 18L3 11.25L4.0605 10.1895L9.75 15.8782L19.9395 5.68945L21 6.74995L9.75 18Z"
                                fill="#103037"
                              />
                            </svg>
                            <p className="m-0 text-[2.25rem]">{check.text}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* ── Trust ─────────────────────────────────────────── */}
      {(data?.trustTitle || data?.trustItems || data?.trustTestimonials) && (
        <section className="relative w-full lg:p-[4.75rem]">
          <div className="flex flex-col px-[3rem] bg-[#103037] py-[7rem] gap-[2rem] lg:rounded-[5rem] lg:py-[15rem] lg:px-[4rem]">
            {data?.trustTitle && (
              <motion.div
                {...fadeUp()}
                className="mx-auto richtext lg:richtext-center richtext-start max-w-[78.375rem] [&_span]:text-[#A3DEB2] lg:prose-h2:text-[5.5rem] prose-h2:text-[4rem] prose-h2:font-[500] prose-h2:text-white prose-h6:font-instrument prose-h6:font-[400] prose-h6:text-[2.25rem] prose-h6:tracking-[0.09rem] prose-h6:text-white prose-h6:mb-[1rem] prose-p:text-white/80 prose-p:w-[min(36.375rem,100%)]"
              >
                <PortableText
                  value={data.trustTitle}
                  components={richTextComponent}
                />
              </motion.div>
            )}
            {data?.trustCta && (
              <motion.div
                {...fadeUp(0.075)}
                className="flex justify-center lg:[&_.button]:!min-h-[5.75rem]"
              >
                <CTAButton data={data.trustCta} />
              </motion.div>
            )}

            <div className="flex flex-col mx-auto w-full gap-[3rem]">
              {/* Trust stats */}
              {data?.trustItems && data.trustItems.length > 0 && (
                <div className="grid grid-cols-1 lg:grid-cols-2 lg:gap-[3rem] lg:mt-[4rem] w-[min(170.5rem,100%)] mx-auto mt-[2rem] gap-[2rem]">
                  {data.trustItems.map((item, idx) => (
                    <motion.div
                      key={idx}
                      {...staggeredFadeUpBlur(idx)}
                      className="flex flex-col-reverse justify-between border lg:flex-row lg:items-center gap-[0.5rem] border-white/20 rounded-[2rem] px-[3rem] py-[3rem] lg:px-[5rem] lg:py-[4.5rem]"
                    >
                      {item.text && (
                        <div className="richtext richtext-start w-[min(36rem,100%)] prose-p:text-white/80 prose-p:text-[2.25rem] prose-p:font-[500]">
                          <PortableText
                            value={item.text}
                            components={richTextComponent}
                          />
                        </div>
                      )}

                      {item.value && (
                        <p className="m-0 text-white font-[400] tracking-[-2.8px] text-[4rem] lg:text-[7rem]">
                          {item.value}
                        </p>
                      )}
                    </motion.div>
                  ))}
                </div>
              )}

              {/* Testimonials */}
              {data?.trustTestimonials && data.trustTestimonials.length > 0 && (
                <div className="overflow-hidden mx-auto w-full max-w-[172.5rem]">
                  <Carousel
                    opts={{ loop: true, align: 'start' }}
                    setApi={setTestimonialsApi}
                    className="w-full"
                  >
                    <CarouselContent className="ml-0">
                      {data.trustTestimonials.map((testimonial, idx) => (
                        <CarouselItem
                          key={idx}
                          className="pl-0 basis-full px-[1rem]"
                        >
                          <div className="flex flex-col lg:flex-row rounded-[2rem] p-[1.5rem] bg-[#85D0A6] gap-[1.5rem] cursor-grab lg:gap-[5rem] lg:h-[74.875rem] lg:rounded-[5rem] lg:p-[2rem] active:cursor-grabbing">
                            {testimonial.image && (
                              <Image
                                src={testimonial.image}
                                alt={''}
                                quality={100}
                                className="object-cover w-full lg:w-auto lg:h-full lg:rounded-none rounded-[1.5rem] max-h-[40rem] shrink-0 lg:max-h-none"
                              />
                            )}
                            <div className="flex flex-col gap-[1.5rem] lg:p-[1rem]">
                              <div className="flex flex-col bg-[#79BA96]/50 rounded-[1.5rem] gap-[2rem] p-[2rem] lg:rounded-[2.75rem] lg:gap-[3rem] lg:p-[5rem]">
                                <svg
                                  className="!shrink-0 w-[3rem] h-[3rem] lg:w-[5.75rem] lg:h-[5.625rem]"
                                  width="46"
                                  height="45"
                                  viewBox="0 0 46 45"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    opacity="0.9"
                                    d="M27.7986 45L35.741 18.9033H27.3022V0H46V16.5613L36.0719 45H27.7986ZM0.496402 45L8.43885 18.9033H0V0H18.6978V16.5613L8.76978 45H0.496402Z"
                                    fill="#080713"
                                  />
                                </svg>
                                {testimonial.quote && (
                                  <div className="richtext richtext-start [&_*]:select-none prose-p:text-[1.75rem] prose-p:font-[400] prose-p:leading-[150%] prose-p:text-[#080713] lg:prose-p:text-[2.25rem]">
                                    <PortableText
                                      value={testimonial.quote}
                                      components={richTextComponent}
                                    />
                                  </div>
                                )}
                              </div>
                              <div className="w-full lg:px-0 px-[1rem]">
                                {testimonial.name && (
                                  <p className="m-0 font-[500] text-[#103037] text-[2.25rem] lg:text-[3rem]">
                                    {testimonial.name}
                                  </p>
                                )}
                                {testimonial.position && (
                                  <p className="m-0 font-[500] text-[#103037]/70 text-[1.75rem] lg:text-[2.25rem]">
                                    {testimonial.position}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        </CarouselItem>
                      ))}
                    </CarouselContent>
                  </Carousel>

                  {/* Arrows + Stepper */}
                  <div className="flex justify-center items-center mt-0 gap-[1.5rem] px-[1rem] lg:gap-[2rem] lg:mt-[4rem] lg:px-[2rem]">
                    {/* Previous Button */}
                    <button
                      type="button"
                      onClick={() => testimonialsApi?.scrollPrev()}
                      className="flex flex-shrink-0 justify-center items-center text-white/50 rounded-full bg-white/[15%] transition-all w-[5rem] h-[5rem]  lg:w-[6rem] lg:h-[6rem] hover:bg-white/10"
                      aria-label="Previous testimonial"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        className="flex-shrink-0 h-auto rotate-180 w-[2.5rem] lg:w-[3rem]"
                      >
                        <path
                          d="M13.5 4.5L12.4275 5.54475L18.1125 11.25H3V12.75H18.1125L12.4275 18.4298L13.5 19.5L21 12L13.5 4.5Z"
                          fill="currentColor"
                        />
                      </svg>
                    </button>

                    {/* Stepper Dots */}
                    <div className="flex items-center gap-[0.75rem] lg:gap-[1rem]">
                      {data.trustTestimonials.map((_, idx) => (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => testimonialsApi?.scrollTo(idx)}
                          className={cn(
                            'rounded-full transition-all duration-300',
                            idx === testimonialsCurrent
                              ? 'w-[7.75rem] h-[.5rem] bg-[#A3DEB2] '
                              : 'w-[7.75rem] h-[.5rem] bg-[#A3DEB2]/30 hover:bg-[#A3DEB2]/50 ',
                          )}
                          aria-label={`Go to testimonial ${idx + 1}`}
                        />
                      ))}
                    </div>

                    {/* Next Button */}
                    <button
                      type="button"
                      onClick={() => testimonialsApi?.scrollNext()}
                      className="flex flex-shrink-0 justify-center items-center rounded-full bg-white/[15%] transition-all text-white/50 w-[5rem] h-[5rem]  lg:w-[6rem] lg:h-[6rem] hover:bg-white/10"
                      aria-label="Next testimonial"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        className="flex-shrink-0 h-auto w-[2.5rem] lg:w-[3rem]"
                      >
                        <path
                          d="M13.5 4.5L12.4275 5.54475L18.1125 11.25H3V12.75H18.1125L12.4275 18.4298L13.5 19.5L21 12L13.5 4.5Z"
                          fill="currentColor"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* ── Proof ─────────────────────────────────────────── */}
      {(data?.proofTitle || data?.proofItems) && (
        <section className="relative w-full py-[6rem] lg:py-[10rem]">
          <div className="container mx-auto px-[2rem] lg:px-[4rem]">
            {data?.proofTitle && (
              <motion.div
                {...fadeUp()}
                className="richtext lg:richtext-center richtext-start lg:mx-auto lg:w-[min(64.25rem,100%)] w-[80%] lg:[&_span]:font-display lg:[&_span]:font-[700] [&_span]:font-instrument-sans [&_span]:font-[600] [&_span]:text-[#103037] lg:[&_span]:text-[#65A38A] lg:[&_br]:block [&_br]:hidden [&_span]:pl-[1rem] lg:[&_span]:pl-0 prose-h1:tracking-[-0.18rem] lg:prose-h1:text-[6rem] prose-h1:text-[4rem] prose-h6:font-instrument prose-h6:font-[400] prose-h6:text-[2.25rem] prose-h6:tracking-[0.09rem] prose-h6:text-black prose-h6:pb-[2rem] prose-p:w-[min(58.125rem,100%)] prose-strong:!font-[600]"
              >
                <PortableText
                  value={data.proofTitle}
                  components={richTextComponent}
                />
              </motion.div>
            )}
            {data?.proofItems && data.proofItems.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-[2rem] mt-[4rem]">
                {data.proofItems.map((item, idx) => (
                  <motion.div
                    key={idx}
                    {...staggeredFadeUpBlur(idx)}
                    className="flex flex-col bg-[#F0F4F2] rounded-[1.5rem] p-[3rem] gap-[4rem] lg:p-[4rem]"
                  >
                    <Image
                      src={lilIcon}
                      alt=""
                      className="hidden p-0 m-0 lg:block w-[4rem] h-[4rem]"
                    />
                    {item.text && (
                      <div className="richtext richtext-start prose-h3:text-[#103037] prose-h3:font-instrument-sans prose-h3:font-[600] prose-h3:text-[3rem] lg:prose-h3:text-[3.5rem] lg:prose-p:w-[80%] prose-p:w-[70%]">
                        <PortableText
                          value={item.text}
                          components={richTextComponent}
                        />
                      </div>
                    )}
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* ── Last ──────────────────────────────────────────── */}
      {(data?.lastTitle || data?.lastCta) && (
        <section className="relative w-full p-[1rem] lg:p-[5rem]">
          <div className="relative w-full text-center bg-gradient-to-b from-[#F6F9F7] to-[#E6F2EB] pt-[6.5rem] rounded-[3rem] h-[82.5rem] overflow-clip lg:h-[119.375rem] lg:rounded-[5.25rem] lg:pt-[12.25rem]">
            <Image
              src={iq}
              alt=""
              className="relative mx-auto z-[5] mb-[2rem]"
            />

            <Image
              src={doubleStatues}
              alt=""
              className="hidden absolute bottom-0 left-1/2 scale-150 -translate-x-1/2 lg:block lg:scale-100 z-[5] w-[135.375rem]"
            />

            <Image
              className="absolute left-1/2 -translate-x-1/2 bottom-0 !w-[max(100%,90rem)] !max-w-none !h-full object-cover object-top mix-blend-luminosity opacity-[.36] z-[1] lg:block hidden"
              src={bg}
              quality={100}
              alt="background art"
            />

            <div className="px-[2.625rem]">
              {data?.lastTitle && (
                <div className="richtext mx-auto lg:[&_span]:font-display lg:[&_span]:font-[700] [&_span]:font-instrument-sans [&_span]:font-[600] [&_span]:text-[#103037] lg:[&_span]:text-[#65A38A] z-[5] relative prose-h1:tracking-[-0.18rem] lg:prose-h1:text-[6rem] prose-h1:text-[5rem] prose-h1:w-[min(58.875rem,100%)] prose-h6:font-instrument prose-h6:font-[400] prose-h6:text-[2.25rem] prose-h6:tracking-[0.09rem] prose-h6:text-black prose-h6:pb-[2rem] prose-p:w-[min(63.625rem,100%)] prose-p:text-[2rem] prose-strong:!font-[600]">
                  <PortableText
                    value={data.lastTitle}
                    components={richTextComponent}
                  />
                </div>
              )}
              {data?.lastCta && (
                <div className="flex relative justify-center mt-[3rem] z-[5] lg:[&_.button]:!min-h-[5.75rem]">
                  <CTAButton data={data.lastCta} />
                </div>
              )}
            </div>

            <Image
              src={lastBG}
              alt=""
              quality={100}
              className="absolute bottom-0 mx-auto w-full lg:hidden z-[5] mb-[2rem] translate-y-[5%]"
            />
          </div>
        </section>
      )}

      {data?.settings?.footer && (
        <Footer
          data={data.settings.footer}
          slug={data?.settings?.slug?.current}
        />
      )}
    </>
  );
}
